import { default as MDIcon } from "@mdi/react";
import React, { useEffect, useRef, useState } from "react";

import { mdiUndo } from "@mdi/js";
import { InputNumber } from "antd";

import { StyledInputContainer, StyledResetButton } from "../shared/StyledComponents";

interface SeriesStyleInputProps {
  value?: number;
  defaultValue: number;
  min?: number;
  max?: number;
  step?: number;
  "data-testid"?: string;
  onChange: (value: number | null) => void;
  onReset?: () => void; // Optional custom reset handler
  showResetButton?: (currentValue: number, defaultValue: number) => boolean; // Custom logic for when to show reset
}

const SeriesStyleInput: React.FC<SeriesStyleInputProps> = ({
  value,
  defaultValue,
  min,
  max,
  step,
  "data-testid": dataTestId,
  onChange,
  onReset,
  showResetButton: showResetWhen
}) => {
  const [currentValue, setCurrentValue] = useState<number>(value ?? defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state with external value prop
  useEffect(() => {
    setCurrentValue(value ?? defaultValue);
  }, [value, defaultValue]);

  // Handle wheel events with non-passive listener to prevent page scroll
  useEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;

    const handleWheel = (e: WheelEvent) => {
      // Only handle wheel events if the input is focused (Ant Design best practice)
      if (document.activeElement !== inputElement) {
        return;
      }

      e.preventDefault(); // Prevent page scroll
      e.stopPropagation();

      const isScrollUp = e.deltaY < 0;
      const stepValue = step || 1;
      const newValue = isScrollUp ? currentValue + stepValue : currentValue - stepValue;

      // Block wheel scroll if it would exceed bounds
      if (min !== undefined && newValue < min) {
        return; // Don't allow scroll below minimum
      }
      if (max !== undefined && newValue > max) {
        return; // Don't allow scroll above maximum
      }

      // Value is within bounds, allow the change
      setCurrentValue(newValue);
      onChange(newValue);
    };

    // Add event listener with passive: false to allow preventDefault
    inputElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      inputElement.removeEventListener("wheel", handleWheel);
    };
  }, [currentValue, step, min, max, onChange]);

  const handleChange = (val: number | null) => {
    // Handle null values (when input is cleared) - don't call onChange for null
    if (val === null) {
      return;
    }

    // Block input if it would exceed bounds
    if (min !== undefined && val < min) {
      return; // Don't allow input below minimum
    }
    if (max !== undefined && val > max) {
      return; // Don't allow input above maximum
    }

    setCurrentValue(val);
    onChange(val);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const inputValue = (e.target as HTMLInputElement).value;

    // Allow control characters (backspace, delete, tab, escape, enter, arrows)
    if (
      key === "Backspace" ||
      key === "Delete" ||
      key === "Tab" ||
      key === "Escape" ||
      key === "Enter" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "ArrowUp" ||
      key === "ArrowDown"
    ) {
      return;
    }

    // Allow Ctrl+key combinations (copy, paste, select all, etc.)
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    // Allow decimal point, but only if not already present
    if (key === "." && inputValue.indexOf(".") !== -1) {
      e.preventDefault();
      return;
    }

    // Allow only digits and decimal point (no negative signs)
    if (!/[0-9.]/.test(key)) {
      e.preventDefault();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow arrow keys to respect min/max bounds
    const isArrowUp = e.key === "ArrowUp";
    const isArrowDown = e.key === "ArrowDown";

    if (isArrowUp || isArrowDown) {
      const stepValue = step || 1;
      const newValue = isArrowUp ? currentValue + stepValue : currentValue - stepValue;

      // Prevent arrow keys from going beyond bounds by preventing default
      if (min !== undefined && newValue < min) {
        e.preventDefault();
        return;
      }
      if (max !== undefined && newValue > max) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleReset = () => {
    if (onReset) {
      // Use custom reset handler if provided
      onReset();
      // Note: The component's internal state will be updated via the useEffect
      // when the external value prop changes as a result of the onReset call
    } else {
      // Default reset behavior
      setCurrentValue(defaultValue);
      onChange(defaultValue);
    }
  };

  const isDefaultValue = showResetWhen
    ? !showResetWhen(currentValue, defaultValue)
    : currentValue === defaultValue;

  return (
    <StyledInputContainer>
      <InputNumber
        ref={inputRef}
        value={currentValue}
        min={min}
        max={max}
        step={step}
        data-testid={dataTestId}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyPress={handleKeyPress}
        controls={false}
      />
      {!isDefaultValue && (
        <StyledResetButton onClick={handleReset} title="Reset to default">
          <MDIcon path={mdiUndo} size={1} />
        </StyledResetButton>
      )}
    </StyledInputContainer>
  );
};

export default SeriesStyleInput;
